import type { MiniLeague } from '@h2h/shared';
import { Avatar, MiniLeagueMark } from '../ui/bits';
import { ArrowDown, ArrowUp, Trophy } from '../ui/icons';
import { useMiniLeagueStandings } from '../../hooks/useQueries';
import { formatCount, ordinalSuffix } from '../../lib/format';
import { cn } from '../../lib/cn';

export function MiniLeagueCard({
  mini,
  uid,
  leagueName,
  onOpen
}: {
  mini: MiniLeague;
  uid: string | null;
  leagueName: string;
  onOpen: () => void;
}) {
  const { data: rows } = useMiniLeagueStandings(uid, mini);
  const list = rows ?? [];
  const me = list.find((r) => r.isYou);
  const leader = list[0];
  const memberCount = mini.memberIds.length;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="tap w-full rounded-[24px] border border-black/6 bg-card p-4 text-left shadow-tile transition-transform active:scale-[0.99]"
    >
      <div className="flex items-center gap-[13px]">
        <MiniLeagueMark icon={mini.icon} color={mini.color} size={46} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[16px] font-extrabold tracking-snug text-ink">{mini.name}</div>
          <div className="truncate text-[12px] font-medium text-ink2">
            {memberCount} {memberCount === 1 ? 'friend' : 'friends'} · {leagueName}
          </div>
        </div>
        <RankChip rank={me?.rank} change={me?.rankChange ?? 0} />
      </div>

      <div className="mt-[13px] flex items-center justify-between">
        <div className="flex items-center">
          {list.slice(0, 3).map((r, i) => (
            <Avatar
              key={r.userId}
              name={r.displayName}
              tone={r.avatarTone}
              size={26}
              className={cn('border-2 border-card', i > 0 && '-ml-2')}
            />
          ))}
          {memberCount > 3 && (
            <div className="-ml-2 grid h-[26px] w-[26px] place-items-center rounded-full border-2 border-card bg-fill2 text-[9px] font-extrabold text-ink2">
              +{memberCount - 3}
            </div>
          )}
        </div>
        <StatusLine
          rank={me?.rank}
          rows={list.map((r) => ({ points: r.seasonPoints, name: r.displayName }))}
          myPoints={me?.seasonPoints ?? 0}
          leaderName={leader?.displayName ?? ''}
        />
      </div>
    </button>
  );
}

function RankChip({ rank, change }: { rank?: number; change: number }) {
  if (rank == null) {
    return <span className="text-[12px] font-semibold text-ink3">—</span>;
  }
  return (
    <div className="text-right">
      <div className="num text-[24px] font-extrabold leading-none text-ink">
        {rank}
        <span className="text-[13px] font-bold text-ink3">{ordinalSuffix(rank)}</span>
      </div>
      <div className="mt-[3px] flex items-center justify-end gap-[3px]">
        {rank === 1 ? (
          <>
            <Trophy width={11} height={11} strokeWidth={2} className="text-gold-bottom" />
            <span className="text-[11px] font-bold text-gold-ink">leading</span>
          </>
        ) : change > 0 ? (
          <>
            <ArrowUp width={11} height={11} strokeWidth={3} className="text-accent-ink" />
            <span className="text-[11px] font-bold text-accent-ink">up {change}</span>
          </>
        ) : change < 0 ? (
          <>
            <ArrowDown width={11} height={11} strokeWidth={3} className="text-danger" />
            <span className="text-[11px] font-bold text-danger">down {Math.abs(change)}</span>
          </>
        ) : (
          <span className="text-[11px] font-semibold text-ink3">steady</span>
        )}
      </div>
    </div>
  );
}

function StatusLine({
  rank,
  rows,
  myPoints,
  leaderName
}: {
  rank?: number;
  rows: { points: number; name: string }[];
  myPoints: number;
  leaderName: string;
}) {
  if (rank == null || rows.length <= 1) {
    return <span className="text-[12px] font-medium text-ink2">Invite mates to start the race</span>;
  }
  const firstName = (n: string) => n.split(/\s+/)[0];
  if (rank === 1) {
    const gap = myPoints - (rows[1]?.points ?? myPoints);
    return (
      <span className="text-[12px] font-bold text-accent-ink">
        {formatCount(gap)} pts clear at the top
      </span>
    );
  }
  if (rank <= 3) {
    const behind = (rows[0]?.points ?? myPoints) - myPoints;
    return (
      <span className="text-[12px] font-medium text-ink2">
        {formatCount(behind)} pts behind <span className="font-bold text-ink">{firstName(leaderName)}</span>
      </span>
    );
  }
  const podium = rows[2]?.points ?? myPoints;
  return (
    <span className="text-[12px] font-medium text-ink2">{formatCount(podium - myPoints)} pts off the podium</span>
  );
}
